import { memo, useCallback, useRef, useState } from "react";
import EmissionFactorsEditor from "./EmissionFactorsEditor";
import { hasGasBreakdown } from "../../model/helpers";
import { KG_CO2E } from "../../../../../_constants/emissionUnits";

/**
 * Every GHG factor this KPI is priced at, one chip each, editable in place.
 *
 * A KPI commonly carries several: one per unit it can be filed in, and one per
 * source database that publishes a figure for it. All of them show, in the
 * order the endpoint returns them (database priority).
 *
 * A KPI that has never been saved can carry factors too: the save writes the
 * KPI first and reads its new id back out of the response, so the factors land
 * in the same action.
 *
 * @param {object} props
 * @param {object[]} props.factors
 * @param {boolean} [props.loading]
 * @param {boolean} [props.failed] the factors did not load; editing would overwrite unseen ones
 * @param {() => void} [props.onRetry]
 * @param {boolean} [props.dirty]
 * @param {string} [props.error]
 * @param {boolean} [props.disabled]
 * @param {{ value: string, label: string }[]} props.scopeOptions
 * @param {string[]} props.countrySuggestions
 * @param {object[]} props.unitMaster
 * @param {object[]} props.databases
 * @param {(action: { type: string, index?: number, patch?: object }) => void} props.onAction must be stable
 */
function EmissionFactorsCell({
  factors,
  loading,
  failed,
  onRetry,
  dirty,
  error,
  disabled,
  scopeOptions,
  countrySuggestions,
  unitMaster,
  databases,
  onAction,
}) {
  const [open, setOpen] = useState(false);
  const cellRef = useRef(null);
  const close = useCallback(() => {
    setOpen(false);
    onAction({ type: "prune" });
  }, [onAction]);

  /** Opening an empty cell adds its first factor too, so there is a row to type in. */
  const openEditor = () => {
    if (disabled) return;
    if (factors.length === 0) onAction({ type: "add" });
    setOpen(true);
  };

  const addFactor = () => {
    onAction({ type: "add" });
    setOpen(true);
  };

  const unitName = (unitId) =>
    unitMaster.find((unit) => unit.id === unitId)?.unit || "?";

  const databaseName = (factor) =>
    factor.databaseName ||
    databases.find((database) => String(database.id) === String(factor.databaseId))?.name ||
    "no source";

  const classes = [
    "kgb-chipcell",
    dirty ? "kgb-chipcell--dirty" : "",
    error ? "kgb-chipcell--invalid" : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (failed) {
    return (
      <div className="kgb-chipcell kgb-chipcell--invalid">
        <span className="kgb-emission--muted">Couldn't load</span>
        <button type="button" className="kgb-add-link" onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className={classes} ref={cellRef}>
      {loading && factors.length === 0 ? <span className="kgb-emission--muted">…</span> : null}

      {factors.map((factor, index) => (
          <button
            type="button"
            key={factor.id ?? `new-${index}`}
            className="kgb-factorchip"
            disabled={disabled}
            onClick={openEditor}
            title={`${factor.scope} · ${factor.countryCode} · ${factor.value} ${KG_CO2E} per ${unitName(
              factor.unitId,
            )} · ${databaseName(factor)}${hasGasBreakdown(factor) ? ` · gas-wise (${factor.gwpSource || "GWP"})` : ""}`}
          >
            <i className="kgb-dot" data-scope={String(factor.scope).replace(/\D/g, "")} aria-hidden="true" />
            <span className="kgb-factorchip-value">{factor.value || "—"}</span>
            <span className="kgb-chip-key">/{unitName(factor.unitId)}</span>
            <span className="kgb-factorchip-meta">{factor.countryCode}</span>
          </button>
      ))}

      {disabled ? null : (
        <button
          type="button"
          className="kgb-add-link"
          aria-expanded={open}
          onClick={factors.length ? addFactor : openEditor}
        >
          {factors.length ? "+ Add" : "+ Add factor"}
        </button>
      )}

      {open ? (
        <EmissionFactorsEditor
          anchorRef={cellRef}
          factors={factors}
          scopeOptions={scopeOptions}
          countrySuggestions={countrySuggestions}
          unitMaster={unitMaster}
          databases={databases}
          error={error}
          onChange={(index, patch) => onAction({ type: "change", index, patch })}
          onRemove={(index) => onAction({ type: "remove", index })}
          onAdd={() => onAction({ type: "add" })}
          onClose={close}
        />
      ) : null}
    </div>
  );
}

export default memo(EmissionFactorsCell);
